import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  AcademicCapIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowRightIcon,
  ArrowRightOnRectangleIcon,
  BookOpenIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  CheckBadgeIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  ClockIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  FireIcon,
  InformationCircleIcon,
  PlayIcon,
  TrophyIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import BrandLogo from '../components/common/BrandLogo';
import NotificationBell from '../components/common/NotificationBell';
import { getCurrentUser } from '../services/authService';
import { getLearningProgress, getLearningSummary, getQuizDatasets } from '../services/quizService';
import {
  abandonExamSession,
  getAllActiveExamSessions,
  getAssignments,
  getAttempts,
  getAttempt,
} from '../services/trainingService';
import { buildStudentNotifications } from '../utils/notificationUtils';
import { getExamQuotaStatus } from '../utils/quotaUtils';
import './TrainingPortal.css';
import './TrainingPortalFlow.css';

const WEEKLY_GOAL = 5;
const DEFAULT_SET_COUNT = 6;
const DEFAULT_QUESTION_COUNT = 50;
const DEFAULT_DURATION_MINUTES = 50;
const PASS_THRESHOLD = 70;

const formatDate = (value) => {
  if (!value) return 'Gần đây';
  try {
    return new Intl.DateTimeFormat('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(value));
  } catch {
    return 'Gần đây';
  }
};

const firstName = (user) => {
  const fullName = (user?.full_name || '').trim();
  if (!fullName || /system administrator/i.test(fullName)) return 'bạn';
  const clean = fullName.replace(/[()[\]{}]/g, '').trim();
  const parts = clean.split(/\s+/).filter(Boolean);
  return parts.length > 0 ? parts[parts.length - 1] : 'bạn';
};

const compactExamTitle = (title) => {
  const fallback = `Generative AI (${DEFAULT_QUESTION_COUNT} câu)`;
  return (title || fallback)
    .replace(/^Đề\s*số\s*\d+\s*[-–]\s*/i, '')
    .replace(/^Đề\s*\d+\s*[-–]\s*/i, '')
    .trim() || fallback;
};

const buildDiceBearAvatarUrl = (user) => {
  if (user?.avatar_url) return user.avatar_url;
  const seed = encodeURIComponent(user?.avatar_seed || user?.email || user?.username || `user-${user?.id || 'sv'}`);
  const style = encodeURIComponent(user?.avatar_style || 'pixel-art');
  return `https://api.dicebear.com/10.x/${style}/svg?seed=${seed}`;
};

const countStreak = (attempts) => {
  const days = new Set(attempts.map((row) => new Date(row.submitted_at).toDateString()));
  let streak = 0;
  const cursor = new Date();
  while (days.has(cursor.toDateString())) {
    streak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
};

const modeLabel = (mode) => (mode === 'mock' ? 'Thi thử' : 'Ôn tập');

const modeToQuery = (mode) => (mode === 'mock' ? 'exam' : 'study');

const getEffectiveUserId = (user) => {
  if (user?.id) return String(user.id);
  try {
    const stored = JSON.parse(localStorage.getItem('user') || 'null');
    if (stored?.id) return String(stored.id);
  } catch {}
  return null;
};

const getStudyStoragePrefix = (user) => {
  const uid = getEffectiveUserId(user);
  return uid ? `sao_viet_study_v5_${uid}_` : null;
};

const getStudyStorageKey = (user, datasetId) => {
  const prefix = getStudyStoragePrefix(user);
  return prefix ? `${prefix}${datasetId || 'default'}_all` : null;
};


const fallbackDatasets = Array.from({ length: DEFAULT_SET_COUNT }, (_, index) => ({
  id: `genai_exam_set_${index + 1}`,
  title: `Đề số ${index + 1} - Generative AI (${DEFAULT_QUESTION_COUNT} câu)`,
  total_questions: DEFAULT_QUESTION_COUNT,
}));

const normalizeExamSession = (session, datasets) => {
  if (!session) return null;
  const dataset = datasets.find((row) => row.id === session.dataset_id);
  const total = session.questions?.length || session.question_ids?.length || DEFAULT_QUESTION_COUNT;
  const answered = Object.keys(session.answers || {}).length;
  return {
    id: session.id,
    mode: 'mock',
    datasetId: session.dataset_id,
    examSetTitle: dataset?.title || 'Đề thi thử Generative AI',
    answeredQuestions: answered,
    totalQuestions: total,
    currentQuestion: Number(session.current_index || 0) + 1,
    progressPercent: total ? Math.round(answered * 100 / total) : 0,
    remainingMinutes: Math.max(1, Math.round((session.remaining_seconds || 0) / 60)),
    isRandom: true,
    status: 'in_progress',
    updatedAt: session.updated_at,
  };
};

const normalizeStudyProgress = (progress) => {
  if (!progress) return null;
  return {
    id: progress.storageKey,
    mode: 'practice',
    datasetId: progress.datasetId,
    examSetTitle: progress.dataset?.title || 'Đề ôn tập Generative AI',
    answeredQuestions: progress.answeredCount,
    totalQuestions: progress.totalQuestions,
    currentQuestion: Number(progress.currentIndex || 0) + 1,
    progressPercent: progress.percent,
    remainingMinutes: null,
    isRandom: Boolean(progress.isRandom),
    status: 'in_progress',
    storageKey: progress.storageKey,
    updatedAt: progress.savedAt,
  };
};

const sessionTimeValue = (session) => {
  if (!session?.updatedAt) return 0;
  const parsed = new Date(session.updatedAt).getTime();
  return Number.isFinite(parsed) ? parsed : Number(session.updatedAt) || 0;
};

const AppHeader = ({ activePage, currentUser, importantNotifications, examAttemptCount = 0, onOpenDashboard, onOpenSelection, onOpenExamHistory, onOpenProgress, smartReviewEnabled, logout }) => (
  <header className="learning-header">
    <button className="learning-brand brand-button" onClick={onOpenDashboard} aria-label="Trung tâm Tin học Sao Việt" type="button">
      <BrandLogo compact />
    </button>
    <nav className="learning-nav" aria-label="Điều hướng học tập">
      <button className={activePage === 'dashboard' ? 'active' : ''} onClick={onOpenDashboard} type="button">Tổng quan</button>
      <button className={activePage === 'selection' ? 'active' : ''} onClick={onOpenSelection} type="button">Bộ đề thi</button>
      <button
        onClick={onOpenExamHistory}
        type="button"
        title="Xem lại lịch sử các lần thi thử trước (không trừ lượt)"
      >
        Lịch sử thi thử {examAttemptCount > 0 ? `(${examAttemptCount})` : ''}
      </button>
      {['admin', 'administrator', 'teacher', 'center'].includes((currentUser?.role || '').toLowerCase()) && (
        <Link to="/admin/accounts" className="learning-nav-admin-link" title="Cổng quản trị hệ thống">
          Quản trị
        </Link>
      )}
    </nav>
    <div className="learning-account">
      <NotificationBell
        notifications={importantNotifications}
        buttonClassName="notification-button"
        emptyText="Chưa có cảnh báo lớp học hoặc tài khoản."
      />
      <span className="header-divider" />
      <div className="learner-avatar" aria-hidden="true">
        <img
          alt=""
          src={buildDiceBearAvatarUrl(currentUser)}
          onError={(event) => {
            event.currentTarget.style.display = 'none';
            event.currentTarget.nextElementSibling.style.display = 'inline';
          }}
        />
        <span>{firstName(currentUser).charAt(0).toUpperCase()}</span>
      </div>
      <strong>{firstName(currentUser)}</strong>
      <button className="logout-button" aria-label="Đăng xuất" onClick={logout} title="Đăng xuất" type="button">
        <ArrowRightOnRectangleIcon />
      </button>
    </div>
  </header>
);

const HeroVisual = () => (
  <div className="dashboard-robot-scene" aria-hidden="true">
    <div className="robot-glow" />
    <div className="robot-star star-a">✦</div>
    <div className="robot-star star-b">✧</div>
    <div className="robot-message">Kiến thức hôm nay<br />là cơ hội của ngày mai!</div>
    <div className="study-robot">
      <div className="robot-antenna"><i /></div>
      <div className="robot-head"><span className="robot-eye left" /><span className="robot-eye right" /><i /></div>
      <div className="robot-body"><img alt="" src="/brand/sao-viet-mark.png" /></div>
      <span className="robot-arm left" /><span className="robot-arm right" />
    </div>
    <div className="robot-laptop"><span /></div>
    <p>Học chủ động<br />Tiến xa hơn mỗi ngày</p>
  </div>
);

const HeroBanner = ({ currentUser, examAttemptCount = 0, onOpenSelection, onOpenExamHistory }) => (
  <section className="dashboard-hero dashboard-premium-hero">
    <div className="hero-content">
      <span className="hero-greeting">Chào mừng trở lại! <span aria-hidden="true">👋</span></span>
      <h1>{firstName(currentUser)}, sẵn sàng luyện tập hôm nay?</h1>
      <p>Hệ thống 300 câu hỏi chuẩn hóa quốc tế chia đều 6 bộ đề thi. Kiên trì hôm nay, chứng chỉ xuất sắc trong tầm tay!</p>
      <div className="hero-quick-actions" style={{ flexWrap: 'wrap' }}>
        <button className="hero-primary-btn" onClick={onOpenSelection} type="button">
          <PlayIcon /> Ôn luyện ngay
        </button>
        <button className="hero-ghost-btn" onClick={onOpenSelection} type="button">
          <DocumentTextIcon /> Xem bộ đề thi
        </button>
        <button className="hero-ghost-btn" onClick={onOpenExamHistory} type="button">
          <ClockIcon /> Xem lại lịch sử thi thử {examAttemptCount > 0 ? `(${examAttemptCount})` : ''}
        </button>
      </div>
    </div>
    <HeroVisual />
  </section>
);

const SelectionHero = ({ hasSavedSessions, hasSelection }) => (
  <section className="dashboard-hero selection-hero selection-premium-hero">
    <div className="hero-content">
      <span className="selection-hero-kicker"><AcademicCapIcon /> Bắt đầu luyện tập</span>
      <h1>Chọn bộ đề và chế độ làm bài</h1>
      <p>Luyện tập đều đặn với 6 bộ đề GenAI Sao Việt. Tiếp tục phiên đang dở hoặc chọn một đề mới phù hợp với mục tiêu hôm nay.</p>
      <div className="selection-steps" aria-label="Quy trình bắt đầu làm bài">
        <span className={hasSavedSessions ? 'is-complete' : 'active'}><b>{hasSavedSessions ? <CheckCircleIcon /> : '1'}</b> Phiên đang dở</span>
        <i />
        <span className={hasSelection ? 'is-complete' : hasSavedSessions ? 'active' : ''}><b>{hasSelection ? <CheckCircleIcon /> : '2'}</b> Chọn đề và chế độ</span>
        <i />
        <span className={hasSelection ? 'active' : ''}><b>3</b> Bắt đầu làm bài</span>
      </div>
    </div>
    <div className="selection-hero-art" aria-hidden="true">
      <div className="hero-orbit orbit-one" />
      <div className="hero-orbit orbit-two" />
      <div className="selection-hero-document"><DocumentTextIcon /><i /><i /><i /></div>
      <div className="selection-hero-cap"><AcademicCapIcon /></div>
      <div className="selection-hero-chart"><ChartBarIcon /><span>70%</span></div>
      <div className="selection-hero-spark spark-one">✦</div>
      <div className="selection-hero-spark spark-two">✧</div>
      <p>Hôm nay<br />bạn tốt hơn<br />hôm qua.</p>
    </div>
  </section>
);

const StatCard = ({ tone, icon: Icon, label, value, unit, desc, onClick }) => (
  <article
    className={`achievement-item ${tone}`}
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    style={onClick ? { cursor: 'pointer' } : undefined}
  >
    <div className="achievement-icon-wrap"><Icon /></div>
    <div className="achievement-meta">
      <span className="achievement-label">{label}</span>
      <strong className="achievement-value">{value}<span className="achievement-unit">{unit}</span></strong>
      <span className="achievement-desc">{desc}</span>
    </div>
  </article>
);

const QuotaStatCard = ({ quota }) => {
  const tone = quota?.isEmpty ? 'red is-quota-empty quota-spotlight empty' : quota?.isLow ? 'orange quota-spotlight low' : 'orange quota-spotlight';
  const usedLabel = !quota ? 'Đang cập nhật lượt thi' : quota.isUnlimited ? `${quota.used || 0} lượt đã thi` : `Đã thi ${quota.used || 0}/${quota.total || 0} lượt`;
  return (
    <article className={`achievement-item ${tone}`}>
      <div className="achievement-icon-wrap"><FireIcon /></div>
      <div className="achievement-meta quota-meta">
        <span className="achievement-label">Lượt thi thử</span>
        <strong className="achievement-value">
          {quota?.isUnlimited ? 'Vô hạn' : quota?.remaining ?? '--'}
          {!quota?.isUnlimited && <span className="achievement-unit">lượt còn lại</span>}
        </strong>
        <span className="achievement-desc">{quota?.isUnlimited ? 'thi thử không giới hạn' : usedLabel}</span>
        {!quota?.isUnlimited && quota && (
          <div className="quota-mini-progress" aria-label={usedLabel}>
            <i style={{ width: `${quota.percentUsed}%` }} />
          </div>
        )}
      </div>
    </article>
  );
};

const ProgressLine = ({ value }) => (
  <div className="progress-track"><i style={{ width: `${Math.min(100, Math.max(0, value || 0))}%` }} /></div>
);

const CurrentSessionCard = ({ session, onContinue, onRestart, onCancel }) => {
  const isMock = session.mode === 'mock';
  return (
    <article className={`current-session-card ${isMock ? 'mock' : 'practice'}`}>
      <div className="task-icon">{isMock ? <ClipboardDocumentCheckIcon /> : <AcademicCapIcon />}</div>
      <div className="task-details">
        <div className="task-title-row">
          <h3>{session.examSetTitle}</h3>
          <span>{modeLabel(session.mode)} đang làm dở</span>
        </div>
        <p>
          {isMock
            ? `Bạn còn ${session.remainingMinutes || DEFAULT_DURATION_MINUTES} phút làm bài. Có thể tiếp tục ngay để không mất mạch làm bài.`
            : `Bạn đang dừng ở câu ${session.currentQuestion}. Hãy tiếp tục để giữ nhịp ôn tập.`}
        </p>
        <div className="task-progress">
          <div><span>{session.answeredQuestions}/{session.totalQuestions} câu</span><strong>{session.progressPercent}%</strong></div>
          <ProgressLine value={session.progressPercent} />
        </div>
        <div className="task-meta">
          {isMock && <span><ClockIcon /> Còn {session.remainingMinutes} phút</span>}
          <span><ArrowPathIcon /> {isMock ? 'Tự động đảo đề' : (session.isRandom ? 'Trộn ngẫu nhiên' : 'Theo thứ tự đề')}</span>
          <span><CheckBadgeIcon /> Cần đạt 70%</span>
          <span><DocumentTextIcon /> {session.totalQuestions} câu</span>
          <span><CheckCircleIcon /> Tiến độ tự động lưu</span>
        </div>
      </div>
      <div className="current-session-actions">
        <button className="task-cta" onClick={() => onContinue(session)} type="button">
          <PlayIcon /> {isMock ? 'Tiếp tục thi thử' : 'Tiếp tục ôn tập'}
        </button>
        <button className="secondary-action" onClick={() => onRestart(session)} type="button">Làm lại</button>
        <button className="danger-action" onClick={() => onCancel(session)} type="button">Hủy phiên</button>
      </div>
    </article>
  );
};

const CurrentSessionsPanel = ({ sessions = [], onOpenSelection, onContinue, onRestart, onCancel }) => {
  const sortedSessions = [...sessions].sort((left, right) => sessionTimeValue(right) - sessionTimeValue(left));
  const hasSessions = sortedSessions.length > 0;
  const newestSession = sortedSessions[0];
  return (
    <article className="focus-panel session-panel">
      <div className="panel-heading">
        <h2>Phiên hiện tại</h2>
        <span className={hasSessions ? 'soft-status warning' : 'soft-status'}>
          {hasSessions ? `${sortedSessions.length} bài đang dở` : 'Chưa có phiên'}
        </span>
      </div>
      {hasSessions ? (
        <div className="current-session-list">
          <CurrentSessionCard
            key={`${newestSession.mode}-${newestSession.datasetId}-${newestSession.id}`}
            session={newestSession}
            onContinue={onContinue}
            onRestart={onRestart}
            onCancel={onCancel}
          />
          {sortedSessions.length > 1 && (
            <button className="session-panel-link" onClick={onOpenSelection} type="button">
              Xem thêm {sortedSessions.length - 1} phiên đang dở <ArrowRightIcon />
            </button>
          )}
        </div>
      ) : (
        <div className="empty-session-state">
          <DocumentTextIcon />
          <div>
            <strong>Chưa có bài làm dở</strong>
            <p>Chọn bộ đề và chế độ trước, sau đó hệ thống mới mở màn làm bài.</p>
          </div>
          <button className="task-cta" onClick={onOpenSelection} type="button"><PlayIcon /> Chọn bộ đề</button>
        </div>
      )}
    </article>
  );
};

const WeeklyGoalCard = ({ stats }) => {
  const weeklyPercent = Math.min(100, Math.round(stats.activeDays * 100 / WEEKLY_GOAL));
  return (
    <article className="weekly-panel">
      <div className="panel-heading">
        <h2>Mục tiêu tuần</h2>
        <span>{stats.activeDays}/{WEEKLY_GOAL} ngày học</span>
      </div>
      <div className="weekly-body">
        <div className="goal-ring" style={{ '--goal-progress': `${weeklyPercent * 3.6}deg` }}>
          <div><strong>{stats.activeDays}/{WEEKLY_GOAL}</strong><span>ngày</span></div>
        </div>
        <div className="goal-facts">
          <div><ClockIcon /><span><strong>{stats.weeklyMinutes || 'Bắt đầu'}</strong>{stats.weeklyMinutes ? 'phút học tuần này' : 'buổi đầu tiên'}</span></div>
          <div><CalendarDaysIcon /><span><strong>{Math.max(0, WEEKLY_GOAL - stats.activeDays)} ngày nữa</strong>để hoàn thành mục tiêu</span></div>
          <div><FireIcon /><span><strong>{stats.streak || 'Tạo chuỗi'}</strong>{stats.streak ? 'ngày liên tiếp' : 'luyện tập mỗi ngày'}</span></div>
        </div>
      </div>
      <div className="goal-message">
        <ChartBarIcon />
        <span>
          <strong>{stats.activeDays ? 'Bạn đang đi đúng hướng!' : 'Khởi động tuần học hiệu quả'}</strong>
          {stats.activeDays ? 'Duy trì thói quen luyện tập để sẵn sàng thi đạt chứng chỉ.' : 'Một buổi ngắn hôm nay cũng tạo nên sự tiến bộ.'}
        </span>
      </div>
    </article>
  );
};

const LearningSuggestions = ({ sessions, attempts, datasets, summary, onContinue, onPracticeAgain, onStartReview, onOpenSelection }) => {
  const latestSession = sessions[0];
  const completedDatasetIds = new Set(attempts.map((row) => row.dataset_id).filter(Boolean));
  const untriedDataset = datasets.find((row) => !completedDatasetIds.has(row.id));
  const lowestAttempt = [...attempts]
    .filter((row) => Number.isFinite(Number(row.score_percent)))
    .sort((left, right) => Number(left.score_percent) - Number(right.score_percent))[0];
  const suggestions = [];
  if (latestSession) suggestions.push({
    id: 'resume', icon: PlayIcon, tone: 'blue', title: 'Tiếp tục phiên đang dở',
    desc: `${compactExamTitle(latestSession.examSetTitle)} · ${latestSession.progressPercent}% hoàn thành`,
    action: 'Tiếp tục', onClick: () => onContinue(latestSession),
  });
  if (summary?.enabled && summary?.weak_count > 0) suggestions.push({
    id: 'review', icon: AcademicCapIcon, tone: 'rose', title: 'Ôn lại câu cần cải thiện',
    desc: `${summary.weak_count} câu đang chờ bạn củng cố`, action: 'Bắt đầu ôn', onClick: onStartReview,
  });
  if (lowestAttempt) suggestions.push({
    id: 'lowest', icon: ChartBarIcon, tone: 'purple', title: 'Củng cố bộ đề điểm thấp',
    desc: `${compactExamTitle(datasets.find((row) => row.id === lowestAttempt.dataset_id)?.title)} · ${lowestAttempt.score_percent}%`,
    action: 'Luyện lại', onClick: () => onPracticeAgain(lowestAttempt.dataset_id),
  });
  if (untriedDataset) suggestions.push({
    id: 'untried', icon: DocumentTextIcon, tone: 'green', title: 'Thử một bộ đề mới',
    desc: compactExamTitle(untriedDataset.title), action: 'Bắt đầu', onClick: () => onPracticeAgain(untriedDataset.id),
  });
  if (!suggestions.length) suggestions.push({
    id: 'start', icon: BookOpenIcon, tone: 'blue', title: 'Bắt đầu buổi học hôm nay',
    desc: 'Chọn một trong 6 bộ đề để tiếp tục tiến bộ.', action: 'Chọn bộ đề', onClick: onOpenSelection,
  });
  return (
    <article className="insight-panel dashboard-suggestions">
      <div className="section-title-row"><div><h2>Gợi ý hôm nay</h2><p>Dựa trên tiến độ và kết quả hiện có của bạn.</p></div></div>
      <div className="suggestion-grid">
        {suggestions.slice(0, 3).map(({ id, icon: Icon, tone, title, desc, action, onClick }) => (
          <button className={`suggestion-card ${tone}`} key={id} onClick={onClick} type="button">
            <span><Icon /></span><strong>{title}</strong><small>{desc}</small><b>{action} <ArrowRightIcon /></b>
          </button>
        ))}
      </div>
    </article>
  );
};

const AchievementPanel = ({ stats }) => {
  const badges = [
    { id: 'first', icon: ClipboardDocumentCheckIcon, title: 'Khởi đầu vững vàng', desc: 'Hoàn thành bài đầu tiên', unlocked: stats.completed > 0 },
    { id: 'pass', icon: TrophyIcon, title: 'Chinh phục 70%', desc: 'Đạt chuẩn ít nhất một lần', unlocked: stats.passed > 0 },
    { id: 'all-sets', icon: CheckBadgeIcon, title: 'Khám phá trọn bộ', desc: 'Hoàn thành cả 6 bộ đề', unlocked: stats.completedSets >= DEFAULT_SET_COUNT },
    { id: 'streak', icon: FireIcon, title: 'Luyện tập liên tục', desc: 'Học 7 ngày liên tiếp', unlocked: stats.streak >= 7 },
  ];
  return (
    <article className="insight-panel achievement-panel">
      <div className="section-title-row"><div><h2>Thành tích của bạn</h2><p>{badges.filter((badge) => badge.unlocked).length}/{badges.length} huy hiệu đã mở khóa</p></div></div>
      <div className="badge-grid">
        {badges.map(({ id, icon: Icon, title, desc, unlocked }) => (
          <div className={`achievement-badge ${unlocked ? 'unlocked' : 'locked'}`} key={id}>
            <span><Icon /></span><strong>{title}</strong><small>{unlocked ? 'Đã mở khóa' : desc}</small>
          </div>
        ))}
      </div>
    </article>
  );
};

const RecentActivityCard = ({ attempts, datasets, onPracticeAgain, onReviewAttempt, onOpenProgress, onOpenSelection }) => (
  <article className="insight-panel" id="recent-activity">
    <div className="section-title-row">
      <h2>Hoạt động gần đây</h2>
      <button className="text-link-button" onClick={onOpenSelection} type="button">Xem bộ đề thi <ArrowRightIcon /></button>
    </div>
    <div className="activity-list">
      {attempts.slice(0, 5).map((row) => {
        const dataset = datasets.find((d) => d.id === row.dataset_id);
        const title = dataset?.title || 'Đề thi Generative AI';
        const isMockExam = row.mode !== 'study' && Boolean(row.attempt_id);
        return (
          <div className="activity-row" key={row.attempt_id || `${row.dataset_id}-${row.submitted_at}`}>
            <div className="activity-icon"><DocumentTextIcon /></div>
            <div className="activity-copy">
              <strong>{title}</strong>
              <span>{formatDate(row.submitted_at)} · {row.mode === 'exam' ? 'Thi thử' : 'Ôn tập'}</span>
            </div>
            <span className={`result-label ${row.passed ? 'passed' : 'practice'}`}>
              {row.passed ? 'Đạt' : 'Cần luyện thêm'}
            </span>
            <strong className="activity-score">{row.score_percent}%</strong>
            <div className="activity-actions">
              {isMockExam && onReviewAttempt && (
                <button
                  className="activity-btn-review"
                  onClick={() => onReviewAttempt(row.dataset_id || datasets[0]?.id, row.attempt_id)}
                  type="button"
                  title="Xem lại chi tiết bài thi thử này (Không trừ lượt)"
                >
                  <ClockIcon />
                  <span>Xem lại</span>
                </button>
              )}
              <button
                className="activity-btn-retry"
                onClick={() => onPracticeAgain(row.dataset_id || datasets[0]?.id)}
                type="button"
              >
                Luyện lại
              </button>
            </div>
          </div>
        );
      })}
      {attempts.length === 0 && (
        <div className="activity-empty">
          <BookOpenIcon />
          <span><strong>Chưa có hoạt động gần đây</strong>Hãy bắt đầu bằng cách chọn bộ đề và chế độ làm bài.</span>
          <button onClick={onOpenSelection} type="button">Bắt đầu</button>
        </div>
      )}
    </div>
  </article>
);

const DashboardSkeleton = () => (
  <div className="dashboard-skeleton" aria-label="Đang tải dữ liệu học tập" aria-busy="true">
    <div className="skeleton-row"><i /><i /></div>
    <div className="skeleton-row wide"><i /><i /></div>
  </div>
);

const PROGRESS_COLORS = ['#2563eb', '#7c3aed', '#059669', '#f97316', '#64748b'];

const scoreDateKey = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
};

const formatChartDate = (value) => {
  const date = new Date(`${value}T00:00:00Z`);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat('vi-VN', { day: '2-digit', month: '2-digit' }).format(date);
};

const aggregateDailyScores = (scores = []) => {
  const grouped = new Map();
  scores.forEach((score) => {
    const date = scoreDateKey(score.submitted_at);
    if (!date) return;
    const current = grouped.get(date) || { date, total: 0, count: 0, modes: new Set() };
    current.total += Number(score.score || 0);
    current.count += 1;
    if (score.mode) current.modes.add(score.mode);
    grouped.set(date, current);
  });
  return [...grouped.values()].map((row) => ({
    date: row.date,
    score: Math.round((row.total / Math.max(1, row.count)) * 10) / 10,
    mode: [...row.modes].map((mode) => (mode === 'exam' ? 'Thi thử' : 'Ôn tập')).join(', '),
    attempts: row.count,
  })).sort((left, right) => left.date.localeCompare(right.date));
};

const Sparkline = ({ scores, color }) => {
  const points = aggregateDailyScores(scores);
  if (points.length < 2) return <div className="sparkline-empty"><span /></div>;
  const path = points.map((point, index) => {
    const x = index * 100 / (points.length - 1);
    const y = 36 - (Math.max(0, Math.min(100, point.score)) * 30 / 100);
    return `${x},${y}`;
  }).join(' ');
  return <svg className="progress-sparkline" viewBox="0 0 100 40" preserveAspectRatio="none" aria-hidden="true"><polyline points={path} style={{ stroke: color }} /></svg>;
};

const ProgressChart = ({ datasets = [] }) => {
  const [selectedDataset, setSelectedDataset] = useState('all');
  const [hiddenDatasets, setHiddenDatasets] = useState(() => new Set());
  const [tooltip, setTooltip] = useState(null);
  const series = useMemo(() => datasets.map((dataset, index) => ({
    ...dataset,
    color: PROGRESS_COLORS[index % PROGRESS_COLORS.length],
    points: aggregateDailyScores(dataset.scores),
  })), [datasets]);
  const visibleSeries = series.filter((row) => (
    (selectedDataset === 'all' || row.dataset_id === selectedDataset) && !hiddenDatasets.has(row.dataset_id)
  ));
  const dates = [...new Set(visibleSeries.flatMap((row) => row.points.map((point) => point.date)))].sort();
  const width = 760;
  const height = 350;
  const padding = { left: 52, right: 22, top: 22, bottom: 45 };
  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;
  const xFor = (date) => padding.left + (dates.length <= 1 ? plotWidth / 2 : dates.indexOf(date) * plotWidth / (dates.length - 1));
  const yFor = (score) => padding.top + (100 - Math.max(0, Math.min(100, score))) * plotHeight / 100;
  const toggleDataset = (datasetId) => {
    setHiddenDatasets((current) => {
      const next = new Set(current);
      if (next.has(datasetId)) next.delete(datasetId); else next.add(datasetId);
      return next;
    });
  };
  return (
    <article className="progress-chart-panel">
      <div className="progress-panel-heading">
        <div><ChartBarIcon /><span><h2>Biểu đồ tiến bộ theo thời gian</h2><small>Nhiều lượt trong cùng ngày được tính theo điểm trung bình.</small></span></div>
        <select value={selectedDataset} onChange={(event) => setSelectedDataset(event.target.value)} aria-label="Lọc biểu đồ theo bộ đề">
          <option value="all">Tất cả bộ đề</option>
          {series.map((row, index) => <option key={row.dataset_id} value={row.dataset_id}>Đề số {index + 1}</option>)}
        </select>
      </div>
      {dates.length ? (
        <>
          <div className="progress-chart-wrap">
            <svg className="progress-line-chart" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Biểu đồ điểm theo thời gian từ 0 đến 100 phần trăm">
              {[0, 20, 40, 60, 80, 100].map((value) => {
                const y = yFor(value);
                return <g key={value}><line x1={padding.left} x2={width - padding.right} y1={y} y2={y} /><text x={padding.left - 12} y={y + 4} textAnchor="end">{value}%</text></g>;
              })}
              {dates.map((date, index) => {
                if (dates.length > 6 && index % Math.ceil(dates.length / 6) !== 0 && index !== dates.length - 1) return null;
                return <text className="chart-date-label" key={date} x={xFor(date)} y={height - 13} textAnchor="middle">{formatChartDate(date)}</text>;
              })}
              {visibleSeries.map((row) => {
                const points = row.points.map((point) => `${xFor(point.date)},${yFor(point.score)}`).join(' ');
                return <g key={row.dataset_id}>
                  {row.points.length > 1 && <polyline className="chart-series-line" points={points} style={{ stroke: row.color }} />}
                  {row.points.map((point) => <circle
                    aria-label={`${row.title}: ${point.score}% ngày ${formatChartDate(point.date)}`}
                    className="chart-point" cx={xFor(point.date)} cy={yFor(point.score)} fill={row.color} key={`${row.dataset_id}-${point.date}`}
                    onBlur={() => setTooltip(null)}
                    onFocus={() => setTooltip({ ...point, title: row.title, color: row.color, x: xFor(point.date), y: yFor(point.score) })}
                    onMouseEnter={() => setTooltip({ ...point, title: row.title, color: row.color, x: xFor(point.date), y: yFor(point.score) })}
                    onMouseLeave={() => setTooltip(null)} r="5" tabIndex="0"
                  />)}
                </g>;
              })}
            </svg>
            {tooltip && <div className="chart-tooltip" style={{ '--tooltip-color': tooltip.color, left: `${tooltip.x * 100 / width}%`, top: `${tooltip.y * 100 / height}%` }}>
              <strong>{tooltip.title}</strong><span>{formatChartDate(tooltip.date)} · {tooltip.score}%</span>
              <small>{tooltip.mode || 'Chưa xác định chế độ'}{tooltip.attempts > 1 ? ` · ${tooltip.attempts} lượt` : ''}</small>
            </div>}
          </div>
          <div className="progress-chart-legend" aria-label="Ẩn hoặc hiện đường biểu đồ">
            {series.map((row, index) => <button className={hiddenDatasets.has(row.dataset_id) ? 'hidden' : ''} key={row.dataset_id} onClick={() => toggleDataset(row.dataset_id)} type="button"><i style={{ background: row.color }} /> Đề số {index + 1}</button>)}
          </div>
        </>
      ) : <div className="progress-chart-empty"><ChartBarIcon /><strong>Chưa có dữ liệu để vẽ biểu đồ</strong><span>Hoàn thành một bài ôn tập hoặc thi thử để bắt đầu theo dõi.</span></div>}
    </article>
  );
};

const ProgressHistory = ({ attempts = [], onOpenAttempt }) => {
  const [expanded, setExpanded] = useState(false);
  const visibleAttempts = expanded ? attempts : attempts.slice(0, 10);
  return <article className="progress-history-panel">
    <div className="progress-panel-heading"><div><ClockIcon /><span><h2>Lịch sử gần đây</h2><small>{attempts.length} bài trong khoảng thời gian đã chọn</small></span></div>
      {attempts.length > 10 && <button className="progress-history-toggle" onClick={() => setExpanded((value) => !value)} type="button">{expanded ? 'Thu gọn' : 'Xem tất cả'} <ArrowRightIcon /></button>}
    </div>
    <div className="progress-history-list">
      {visibleAttempts.map((row) => <button className="progress-history-row" key={row.attempt_id} onClick={() => onOpenAttempt(row.attempt_id)} type="button">
        <span className="progress-history-icon"><DocumentTextIcon /></span>
        <span className="progress-history-copy"><strong>{row.dataset_title}</strong><small>{formatDate(row.submitted_at)} · {row.mode === 'exam' ? 'Thi thử' : 'Ôn tập'}</small></span>
        <b className={row.passed ? 'passed' : ''}>{row.score_percent}%</b><ArrowRightIcon />
      </button>)}
      {!attempts.length && <div className="progress-history-empty"><DocumentTextIcon /><strong>Chưa có bài đã hoàn thành</strong><span>Lịch sử sẽ xuất hiện sau khi bạn nộp bài đầu tiên.</span></div>}
    </div>
  </article>;
};

const ProgressView = ({ progress, period, loading, error, onRetry, onPeriodChange, onOpenAttempt, onOpenDashboard }) => {
  const datasetRows = progress?.dataset_progress || [];
  return <section className="progress-view progress-premium-page">
    <div className="progress-hero progress-premium-hero">
      <div className="progress-hero-copy"><span className="eyebrow">Hành trình của bạn</span><h1>Tiến bộ học tập</h1><p>Theo dõi điểm số và mở lại từng bài đã hoàn thành.</p></div>
      <div className="progress-trophy-scene" aria-hidden="true"><i className="trophy-ray ray-one" /><i className="trophy-ray ray-two" /><div><TrophyIcon /><span>★</span></div><p>Kiên trì mỗi ngày<br />Tốt hơn hôm qua!</p></div>
      <select value={period} onChange={(event) => onPeriodChange(event.target.value)} aria-label="Khoảng thời gian">
        <option value="7d">7 ngày</option><option value="30d">30 ngày</option><option value="90d">90 ngày</option><option value="all">Tất cả</option>
      </select>
    </div>
    {error && <div className="progress-error" role="alert"><ExclamationTriangleIcon /><span><strong>Không tải được dữ liệu tiến bộ</strong>{error}</span><button onClick={onRetry} type="button">Thử lại</button></div>}
    {loading ? <div className="progress-loading-skeleton" aria-busy="true"><div /><div /><div /></div> : <>
      <div className="progress-set-grid">
        {datasetRows.map((row, index) => <article className={`progress-set-card tone-${index + 1}`} key={row.dataset_id}>
          <div className="progress-set-title"><span><DocumentTextIcon /></span><b>Đề số {index + 1}</b></div>
          <strong>{row.latest_score === null ? 'Chưa thi' : `${row.latest_score}%`}</strong>
          <small>{row.attempt_count} lượt · {row.improvement >= 0 ? '+' : ''}{row.improvement}% thay đổi</small>
          <Sparkline scores={row.scores || []} color={PROGRESS_COLORS[index % PROGRESS_COLORS.length]} />
        </article>)}
      </div>
      <div className="progress-main-grid"><ProgressChart datasets={datasetRows} /><ProgressHistory attempts={progress?.recent_attempts || []} onOpenAttempt={onOpenAttempt} /></div>
      <div className="progress-motivation-banner"><div className="motivation-mark"><TrophyIcon /></div><div><strong>Tiến bộ hôm nay là thành công ngày mai!</strong><span>Hãy duy trì thói quen học tập để đạt kết quả tốt hơn.</span></div><button onClick={onOpenDashboard} type="button"><ArrowLeftIcon /> Quay lại tổng quan</button></div>
    </>}
  </section>;
};

const AttemptDetailModal = ({ detail, onClose, onReviewAttempt }) => detail ? (
  <div className="session-modal-backdrop" role="presentation" onClick={onClose}><section className="session-modal-card attempt-detail-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
    <button className="modal-close" onClick={onClose} aria-label="Đóng" type="button"><XMarkIcon /></button>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', marginBottom: '8px', paddingRight: '32px' }}>
      <div>
        <span className="eyebrow">Chi tiết bài đã làm</span>
        <h2 style={{ margin: '4px 0 0' }}>{detail.score_percent}% · {detail.correct_questions}/{detail.total_questions} câu đúng</h2>
      </div>
      {detail.mode !== 'study' && detail.attempt_id && onReviewAttempt && (
        <button
          type="button"
          className="task-cta"
          onClick={() => {
            onClose();
            onReviewAttempt(detail.dataset_id, detail.attempt_id);
          }}
          style={{ padding: '9px 16px', fontSize: '13.5px' }}
        >
          <ClipboardDocumentCheckIcon /> Xem lại giao diện bài thi →
        </button>
      )}
    </div>
    <div className="attempt-question-list">{(detail.details || []).map((row, index) => <div className={row.is_correct ? 'correct' : 'wrong'} key={row.question_id}><strong>Câu {row.question_index || index + 1}: {row.is_correct ? 'Đúng' : 'Sai'}</strong>{row.question_text && <p className="attempt-question-text">{row.question_text}</p>}<p>{row.feedback || 'Không có giải thích.'}</p></div>)}</div>
  </section></div>
) : null;

const ExamHistoryModal = ({ dataset, datasets = [], attempts, onClose, onReviewAttempt }) => {
  const [filterDatasetId, setFilterDatasetId] = useState('all');

  useEffect(() => {
    if (dataset && typeof dataset === 'object' && dataset.id) {
      setFilterDatasetId(dataset.id);
    } else {
      setFilterDatasetId('all');
    }
  }, [dataset]);

  if (!dataset) return null;

  const allExamAttempts = (attempts || []).filter((row) => row.mode !== 'study');
  const filteredAttempts = filterDatasetId === 'all'
    ? allExamAttempts
    : allExamAttempts.filter((row) => row.dataset_id === filterDatasetId);

  // Calculate attempt numbers per dataset (oldest = #1, newest = #N)
  const attemptNumById = {};
  const groupedBySet = {};
  allExamAttempts.forEach((att) => {
    const dsId = att.dataset_id || 'default';
    if (!groupedBySet[dsId]) groupedBySet[dsId] = [];
    groupedBySet[dsId].push(att);
  });
  Object.values(groupedBySet).forEach((list) => {
    const total = list.length;
    list.forEach((att, idx) => {
      attemptNumById[att.attempt_id] = total - idx;
    });
  });

  const activeSetObj = (datasets || []).find((d) => d.id === filterDatasetId);

  return (
    <div className="session-modal-backdrop" role="presentation" onClick={onClose}>
      <section
        className="session-modal-card attempt-detail-modal"
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
        style={{ maxWidth: '720px', width: '94vw' }}
      >
        <button className="modal-close" onClick={onClose} aria-label="Đóng" type="button"><XMarkIcon /></button>
        <span className="eyebrow">Lịch sử các lần thi thử · Xem lại hoàn toàn miễn phí (Không trừ lượt)</span>
        <h2 style={{ margin: '4px 0 6px' }}>
          {filterDatasetId === 'all'
            ? `Lịch sử Thi thử (${allExamAttempts.length} lượt đã hoàn thành)`
            : compactExamTitle(activeSetObj?.title || dataset?.title)}
        </h2>
        <p style={{ margin: '0 0 12px', fontSize: '13.5px', color: '#64748b' }}>
          Bấm vào nút <strong>Xem lại bài thi</strong> ở bất kỳ lần thi nào bên dưới để xem lại toàn bộ 50 câu hỏi, đáp án bạn đã chọn và lời giải chi tiết (không bị trừ lượt thi thử).
        </p>

        {/* FILTER BY EXAM SET */}
        {datasets && datasets.length > 0 && (
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '14px' }}>
            <button
              type="button"
              onClick={() => setFilterDatasetId('all')}
              style={{
                padding: '6px 12px',
                borderRadius: '999px',
                border: '1px solid',
                borderColor: filterDatasetId === 'all' ? '#2563eb' : '#cbd5e1',
                background: filterDatasetId === 'all' ? '#2563eb' : '#ffffff',
                color: filterDatasetId === 'all' ? '#ffffff' : '#334155',
                fontSize: '12.5px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Tất cả ({allExamAttempts.length})
            </button>
            {datasets.map((ds, idx) => {
              const count = (groupedBySet[ds.id] || []).length;
              const isActive = filterDatasetId === ds.id;
              return (
                <button
                  key={ds.id}
                  type="button"
                  onClick={() => setFilterDatasetId(ds.id)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '999px',
                    border: '1px solid',
                    borderColor: isActive ? '#2563eb' : '#cbd5e1',
                    background: isActive ? '#2563eb' : '#ffffff',
                    color: isActive ? '#ffffff' : '#334155',
                    fontSize: '12.5px',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  Đề số {idx + 1} ({count})
                </button>
              );
            })}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '55vh', overflowY: 'auto', paddingRight: '4px' }}>
          {filteredAttempts.length === 0 ? (
            <div style={{ padding: '28px 16px', textAlign: 'center', color: '#64748b', background: '#f8fafc', borderRadius: '12px', border: '1px dashed #cbd5e1' }}>
              Chưa có lượt thi thử nào cho mục này.
            </div>
          ) : (
            filteredAttempts.map((att, idx) => {
              const attemptNumber = attemptNumById[att.attempt_id] || (filteredAttempts.length - idx);
              const durationMin = att.duration_seconds ? Math.max(1, Math.round(att.duration_seconds / 60)) : null;
              const dsIndex = (datasets || []).findIndex((d) => d.id === att.dataset_id);
              const setShortName = dsIndex >= 0 ? `Đề số ${dsIndex + 1}` : 'Đề thi thử';
              return (
                <div
                  key={att.attempt_id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px',
                    flexWrap: 'wrap',
                    padding: '12px 16px',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    background: '#f8fafc',
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span
                        style={{
                          fontSize: '11.5px',
                          fontWeight: 800,
                          padding: '2px 8px',
                          borderRadius: '6px',
                          background: '#eff6ff',
                          color: '#1d4ed8',
                          border: '1px solid #bfdbfe',
                        }}
                      >
                        {setShortName}
                      </span>
                      <strong style={{ fontSize: '14.5px', color: '#0f172a' }}>Lần thi #{attemptNumber}</strong>
                      <span
                        style={{
                          fontSize: '12px',
                          fontWeight: 700,
                          padding: '2px 9px',
                          borderRadius: '999px',
                          background: att.passed ? '#dcfce7' : '#fee2e2',
                          color: att.passed ? '#15803d' : '#b91c1c',
                        }}
                      >
                        {att.passed ? '✓ Đạt chuẩn' : '✕ Chưa đạt'} · {att.score_percent}%
                      </span>
                    </div>
                    <span style={{ fontSize: '12.5px', color: '#64748b' }}>
                      Đúng {att.correct_questions}/{att.total_questions} câu
                      {durationMin ? ` · Thời gian làm: ${durationMin} phút` : ''}
                      {att.submitted_at ? ` · Nộp ngày: ${formatDate(att.submitted_at)}` : ''}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      if (onReviewAttempt) onReviewAttempt(att.dataset_id, att.attempt_id);
                    }}
                    style={{
                      background: '#2563eb',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '9px 15px',
                      fontSize: '13px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    <ClipboardDocumentCheckIcon style={{ width: '16px', height: '16px' }} /> Xem lại bài thi
                  </button>
                </div>
              );
            })
          )}
        </div>
      </section>
    </div>
  );
};

const ExamSetCard = ({
  dataset,
  index,
  attempts,
  practiceSession,
  mockSession,
  selected,
  selectedMode,
  mockDisabled,
  onSelect,
  onSelectMode,
  onReviewAttempt,
  onOpenHistory,
}) => {
  const setAttempts = attempts.filter((row) => row.dataset_id === dataset.id && row.mode !== 'study');
  const best = setAttempts.length ? Math.max(...setAttempts.map((row) => row.score_percent || 0)) : null;
  const latestAttempt = setAttempts[0] || null;

  let statusText = 'Chưa thi';
  if (practiceSession && mockSession) {
    statusText = `Đang dở 2 bài (Ôn ${practiceSession.answeredQuestions}/${practiceSession.totalQuestions}, Thi còn ${mockSession.remainingMinutes}p)`;
  } else if (practiceSession) {
    statusText = `Đang ôn: ${practiceSession.answeredQuestions}/${practiceSession.totalQuestions} câu`;
  } else if (mockSession) {
    statusText = `Đang thi: còn ${mockSession.remainingMinutes} phút`;
  } else if (best !== null) {
    statusText = `Đã thi ${setAttempts.length} lần · Cao nhất: ${best}%`;
  }

  const hasAnyActive = Boolean(practiceSession || mockSession);
  const accents = ['#2563eb', '#7c3aed', '#059669', '#ea580c', '#e11d48'];

  return (
    <article
      aria-label={`Chọn đề số ${index + 1}`}
      aria-pressed={selected}
      className={`selection-set-card ${selected ? 'selected' : ''} ${hasAnyActive ? 'has-active' : ''}`}
      onClick={() => onSelect(dataset.id)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect(dataset.id);
        }
      }}
      role="button"
      style={{ '--set-accent': accents[index % accents.length] }}
      tabIndex={0}
    >
      <span className="set-select-check">{selected ? <CheckCircleIcon /> : null}</span>
      <div className="set-card-header">
        <div className={`set-icon-tone tone-${(index % 5) + 1}`}><DocumentTextIcon /></div>
        <span className="set-number-badge">Đề số {index + 1}</span>
      </div>
      <h3>{compactExamTitle(dataset.title)}</h3>
      <div className="selection-set-meta">
        <span><DocumentTextIcon /> {dataset.total_questions || DEFAULT_QUESTION_COUNT} câu</span>
        <span><ClockIcon /> {DEFAULT_DURATION_MINUTES} phút</span>
        <span><CheckBadgeIcon /> Đạt {PASS_THRESHOLD}%</span>
      </div>
      <small className={hasAnyActive ? 'status-active-badge' : ''}>{statusText}</small>
      <div className="set-mode-actions" onClick={(event) => event.stopPropagation()}>
        <button
          className={`set-mode-option practice ${selected && selectedMode === 'practice' ? 'active' : ''} ${practiceSession ? 'in-progress' : ''}`}
          onClick={() => onSelectMode(dataset.id, 'practice')}
          type="button"
          title={practiceSession ? `Đang ôn dở câu ${practiceSession.currentQuestion}` : 'Ôn tập'}
        >
          <AcademicCapIcon /> {practiceSession ? 'Tiếp tục ôn' : 'Ôn tập'}
        </button>
        <button
          className={`set-mode-option mock ${selected && selectedMode === 'mock' ? 'active' : ''} ${mockSession ? 'in-progress' : ''}`}
          disabled={mockDisabled}
          onClick={() => onSelectMode(dataset.id, 'mock')}
          type="button"
          title={mockDisabled ? 'Bạn đã hết lượt thi thử' : mockSession ? `Đang thi dở còn ${mockSession.remainingMinutes} phút` : 'Thi thử'}
        >
          <ClipboardDocumentCheckIcon /> {mockSession ? 'Tiếp tục thi' : 'Thi thử'}
        </button>
      </div>
      {latestAttempt && (
        <div
          onClick={(event) => event.stopPropagation()}
          style={{
            display: 'flex',
            gap: '6px',
            marginTop: '8px',
            paddingTop: '8px',
            borderTop: '1px dashed #e2e8f0',
            width: '100%',
          }}
        >
          <button
            type="button"
            onClick={() => onReviewAttempt && onReviewAttempt(dataset.id, latestAttempt.attempt_id)}
            title="Xem lại bài thi thử gần nhất (không trừ lượt)"
            style={{
              flex: 1,
              background: '#eff6ff',
              color: '#1d4ed8',
              border: '1px solid #bfdbfe',
              borderRadius: '8px',
              padding: '6px 10px',
              fontSize: '12px',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '5px',
              whiteSpace: 'nowrap',
            }}
          >
            <ClockIcon style={{ width: '14px', height: '14px', flexShrink: 0 }} />
            <span>Xem lại ({latestAttempt.score_percent}%)</span>
          </button>
          {setAttempts.length > 1 && (
            <button
              type="button"
              onClick={() => onOpenHistory && onOpenHistory(dataset)}
              title="Xem tất cả các lần đã thi thử đề này"
              style={{
                background: '#f8fafc',
                color: '#334155',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                padding: '6px 9px',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {setAttempts.length} lần ▾
            </button>
          )}
        </div>
      )}
    </article>
  );
};

const extractSetShortLabel = (title = '') => {
  const match = String(title).match(/Đề\s*(?:số\s*)?\d+/i);
  return match ? match[0] : compactExamTitle(title);
};

const SavedSessionCard = ({ title, session, mode, onContinue, onRestart }) => (
  <article className={`saved-session-card ${mode}`}>
    <div className="saved-session-header">
      <div className={`saved-session-icon ${mode}`}>{mode === 'mock' ? <ClipboardDocumentCheckIcon /> : <AcademicCapIcon />}</div>
      <div className="saved-session-heading">
        <div className="task-title-row">
          <h3>{title}</h3>
          <span className="saved-session-status">{session ? 'Phiên đang dở' : 'Chưa có phiên'}</span>
        </div>
        {session && <strong>{session.examSetTitle}</strong>}
      </div>
    </div>
    <div className="saved-session-body">
      {session ? (
        <>
          <div className="saved-session-meta-row">
            <span className="session-meta-chip highlight">
              {session.answeredQuestions}/{session.totalQuestions} câu ({session.progressPercent}%)
            </span>
            {session.mode === 'practice' && session.currentQuestion ? (
              <span className="session-meta-chip">Câu {session.currentQuestion}</span>
            ) : null}
            {session.mode === 'mock' && session.remainingMinutes ? (
              <span className="session-meta-chip warning">Còn {session.remainingMinutes} phút</span>
            ) : null}
            <span className={`session-meta-chip ${session.mode === 'mock' ? 'warning' : (session.isRandom ? 'info' : 'success')}`}>
              {session.mode === 'mock'
                ? 'Đề trộn ngẫu nhiên'
                : (session.isRandom ? 'Trộn ngẫu nhiên' : 'Theo thứ tự đề')}
            </span>
          </div>
          <ProgressLine value={session.progressPercent} />
        </>
      ) : (
        <p>{mode === 'practice' ? 'Bạn chưa có phiên ôn tập nào đang lưu.' : 'Bạn chưa có phiên thi thử nào đang lưu.'}</p>
      )}
    </div>
    {session && (
      <div className="saved-session-actions">
        <button className="task-cta" onClick={() => onContinue(session)} type="button">
          <PlayIcon /> Tiếp tục phiên này
        </button>
        {onRestart && (
          <button className="secondary-action" onClick={() => onRestart(session)} type="button">
            Làm lại
          </button>
        )}
      </div>
    )}
  </article>
);

const ConfirmReplaceSessionModal = ({
  intent,
  randomizePractice,
  onChangeRandomizePractice,
  onClose,
  onContinue,
  onCreateNew,
}) => {
  if (!intent) return null;
  const { existingSession, selectedDataset, mode } = intent;
  return (
    <div className="session-modal-backdrop" role="presentation" onClick={onClose}>
      <section className="session-modal-card" role="dialog" aria-modal="true" aria-labelledby="replace-session-title" onClick={(event) => event.stopPropagation()}>
        <button className="modal-close" onClick={onClose} aria-label="Đóng" type="button"><XMarkIcon /></button>
        <div className="modal-icon warning"><ExclamationTriangleIcon /></div>
        <h2 id="replace-session-title">Đang có bài làm dở</h2>
        <p>
          Bạn đang có một phiên {modeLabel(mode)} làm dở của <strong>{selectedDataset?.title || existingSession.examSetTitle}</strong>. Bạn muốn tiếp tục bài đang dở hay bắt đầu lại từ đầu?
        </p>
        <div className="modal-session-summary">
          <strong>{existingSession.examSetTitle}</strong>
          <span>
            {existingSession.answeredQuestions}/{existingSession.totalQuestions} câu
            {existingSession.remainingMinutes ? ` · Còn ${existingSession.remainingMinutes} phút` : ''}
            {mode === 'practice'
              ? ` · Phiên đang lưu: ${existingSession.isRandom ? 'Trộn ngẫu nhiên' : 'Theo thứ tự đề'}`
              : ' · Tự động trộn câu hỏi và đáp án'}
          </span>
        </div>
        {mode === 'practice' ? (
          <label className="modal-random-checkbox">
            <input
              type="checkbox"
              checked={randomizePractice}
              onChange={(event) => onChangeRandomizePractice(event.target.checked)}
            />
            <div>
              <strong>Trộn ngẫu nhiên câu hỏi và đáp án khi làm lại từ đầu</strong>
              <small>
                {randomizePractice
                  ? 'Đang bật: Đảo ngẫu nhiên vị trí câu hỏi và các phương án trả lời.'
                  : 'Đang tắt: Giữ nguyên thứ tự câu hỏi và các phương án theo đúng bộ đề gốc.'}
              </small>
            </div>
          </label>
        ) : (
          <div className="modal-mock-random-note">
            <InformationCircleIcon />
            <span>
              <strong>Lưu ý Thi thử:</strong> Nếu chọn <em>Làm lại từ đầu</em>, hệ thống sẽ tạo đề thi mới và tự động trộn ngẫu nhiên câu hỏi cùng các phương án trả lời.
            </span>
          </div>
        )}
        <div className="modal-actions">
          <button className="task-cta" onClick={() => onContinue(existingSession)} type="button"><PlayIcon /> Tiếp tục bài đang dở</button>
          <button className="danger-action solid" onClick={onCreateNew} type="button">Làm lại từ đầu</button>
          <button className="secondary-action" onClick={onClose} type="button">Hủy</button>
        </div>
      </section>
    </div>
  );
};

const ConfirmCancelSessionModal = ({ session, onClose, onConfirm }) => {
  if (!session) return null;
  return (
    <div className="session-modal-backdrop" role="presentation" onClick={onClose}>
      <section className="session-modal-card" role="dialog" aria-modal="true" aria-labelledby="cancel-session-title" onClick={(event) => event.stopPropagation()}>
        <button className="modal-close" onClick={onClose} aria-label="Đóng" type="button"><XMarkIcon /></button>
        <div className="modal-icon danger"><ExclamationTriangleIcon /></div>
        <h2 id="cancel-session-title">Hủy phiên làm bài?</h2>
        <p>Bạn có chắc muốn hủy phiên {modeLabel(session.mode)} này không? Tiến độ hiện tại sẽ bị mất.</p>
        <div className="modal-session-summary">
          <strong>{session.examSetTitle}</strong>
          <span>{session.answeredQuestions}/{session.totalQuestions} câu{session.remainingMinutes ? ` · Còn ${session.remainingMinutes} phút` : ''}</span>
        </div>
        <div className="modal-actions">
          <button className="danger-action solid" onClick={onConfirm} type="button">Hủy phiên</button>
          <button className="secondary-action" onClick={onClose} type="button">Quay lại</button>
        </div>
      </section>
    </div>
  );
};

const TrainingPortal = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, logout, updateCurrentUser } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [activeExamSessions, setActiveExamSessions] = useState([]);
  const [learningSummary, setLearningSummary] = useState(null);
  const [learningProgress, setLearningProgress] = useState(null);
  const [progressPeriod, setProgressPeriod] = useState('30d');
  const [progressLoading, setProgressLoading] = useState(false);
  const [progressError, setProgressError] = useState('');
  const [attemptDetail, setAttemptDetail] = useState(null);
  const [historyModalDataset, setHistoryModalDataset] = useState(() =>
    location.state?.openExamHistory ? { id: 'ALL', title: 'Tất cả bộ đề' } : null
  );
  const [page, setPage] = useState(() => location.state?.page || 'dashboard');
  const [selectedExamSetId, setSelectedExamSetId] = useState('');
  const [selectedMode, setSelectedMode] = useState('');
  const [randomizePractice, setRandomizePractice] = useState(false);
  const [replaceIntent, setReplaceIntent] = useState(null);
  const [cancelSession, setCancelSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const updateCurrentUserRef = useRef(updateCurrentUser);
  useEffect(() => {
    updateCurrentUserRef.current = updateCurrentUser;
  }, [updateCurrentUser]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [assignmentRows, attemptRows, datasetRows, freshUser, activeSessionsData, summaryData] = await Promise.all([
        getAssignments(true).catch(() => []),
        getAttempts(100, true).catch(() => []),
        getQuizDatasets().catch(() => []),
        getCurrentUser().catch(() => null),
        getAllActiveExamSessions().catch(() => []),
        getLearningSummary().catch(() => null),
      ]);

      if (freshUser && updateCurrentUserRef.current) {
        updateCurrentUserRef.current(freshUser);
      }

      setAssignments(assignmentRows || []);
      setAttempts(attemptRows || []);
      setDatasets(datasetRows?.length ? datasetRows : fallbackDatasets);
      setActiveExamSessions(Array.isArray(activeSessionsData) ? activeSessionsData : (activeSessionsData ? [activeSessionsData] : []));
      setLearningSummary(summaryData);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Không tải được dữ liệu học tập.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const handleStorageOrFocus = () => {
      load();
    };
    window.addEventListener('storage', handleStorageOrFocus);
    window.addEventListener('focus', handleStorageOrFocus);
    return () => {
      window.removeEventListener('storage', handleStorageOrFocus);
      window.removeEventListener('focus', handleStorageOrFocus);
    };
  }, [load]);

  const coreExamSets = useMemo(() => {
    const source = datasets.length ? datasets : fallbackDatasets;
    const list = source.filter((dataset) => dataset.id && dataset.id.startsWith('genai_exam_set_'));
    return (list.length ? list : source).slice(0, DEFAULT_SET_COUNT);
  }, [datasets]);

  const getStudyProgress = useCallback((datasetId) => {
    try {
      // Purge any legacy un-scoped or contaminated v3/v4 study keys
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i);
        if (
          k &&
          (k.startsWith('sao_viet_study_v3_') ||
            k.startsWith('sao_viet_study_v4_') ||
            k.startsWith('sao_viet_study_v5_guest_') ||
            k === 'sao_viet_quiz_state_v3')
        ) {
          localStorage.removeItem(k);
        }
      }

      const uid = getEffectiveUserId(currentUser);
      if (!uid) return null;

      const userPrefix = `sao_viet_study_v5_${uid}_`;
      const primaryKey = `${userPrefix}${datasetId || 'default'}_all`;
      const allMatchingKeys = [primaryKey];

      // Scan localStorage ONLY for keys strictly belonging to this user's ID
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (!k) continue;
        if (k.startsWith(userPrefix) && k.includes(datasetId)) {
          allMatchingKeys.push(k);
        }
      }

      const keysToExamine = Array.from(new Set(allMatchingKeys));
      const dataset = coreExamSets.find((row) => row.id === datasetId);
      const totalQuestions = Number(dataset?.total_questions || DEFAULT_QUESTION_COUNT);
      let bestProgress = null;
      let latestSavedTime = -1;

      for (const storageKey of keysToExamine) {
        const raw = localStorage.getItem(storageKey);
        if (!raw) continue;
        let data = null;
        try {
          data = JSON.parse(raw);
        } catch {
          continue;
        }
        if (!data || typeof data !== 'object') continue;
        if (data.ownerUserId && String(data.ownerUserId) !== String(uid)) continue;

        const answersCount = Object.keys(data.answers || {}).length;
        const resultsCount = Object.keys(data.results || {}).length;
        const answeredIds = new Set([
          ...Object.keys(data.answers || {}),
          ...Object.keys(data.results || {}),
        ]);
        const answeredCount = answeredIds.size;
        const parsedTime = data.savedAt ? new Date(data.savedAt).getTime() : 0;
        const savedTime = Number.isFinite(parsedTime) && parsedTime > 0 ? parsedTime : 1;

        const isFullyAnsweredAndGraded =
          totalQuestions > 0 && answeredCount >= totalQuestions && resultsCount >= totalQuestions;
        const hasSubmittedAttemptAfterSave =
          savedTime > 1 &&
          attempts.some((row) => {
            if (row.mode !== 'study' || row.dataset_id !== datasetId || !row.submitted_at) {
              return false;
            }
            const submittedTime = new Date(row.submitted_at).getTime();
            return Number.isFinite(submittedTime) && submittedTime >= savedTime - 30000;
          });

        if (Boolean(data.completed) || isFullyAnsweredAndGraded || hasSubmittedAttemptAfterSave) {
          localStorage.removeItem(storageKey);
          continue;
        }

        const markedCount = Array.isArray(data.marked)
          ? data.marked.length
          : Object.keys(data.marked || {}).length;
        const hasCurrentQuestion = Boolean(data.currentQuestionId);
        const currentIndex = typeof data.currentIndex === 'number' ? data.currentIndex : 0;
        const hasProgress =
          answersCount > 0 ||
          answeredCount > 0 ||
          markedCount > 0 ||
          hasCurrentQuestion ||
          currentIndex > 0;

        if (hasProgress) {
          if (savedTime > latestSavedTime) {
            latestSavedTime = savedTime;
            bestProgress = {
              datasetId,
              answeredCount,
              totalQuestions,
              percent: totalQuestions ? Math.min(100, Math.round((answeredCount * 100) / totalQuestions)) : 0,
              currentIndex,
              currentQuestionId: data.currentQuestionId,
              isRandom: Boolean(data.isRandom),
              savedAt: data.savedAt || new Date().toISOString(),
              storageKey,
              dataset,
            };
          }
        }
      }

      return bestProgress;
    } catch (e) {
      console.warn('Error reading study progress for', datasetId, e);
    }
    return null;
  }, [attempts, coreExamSets, currentUser]);

  const activePracticeSessions = useMemo(() => {
    return coreExamSets
      .map((dataset) => {
        const p = getStudyProgress(dataset.id);
        return p ? normalizeStudyProgress(p) : null;
      })
      .filter(Boolean);
  }, [coreExamSets, getStudyProgress]);

  const activeMockSessions = useMemo(() => {
    return (activeExamSessions || [])
      .map((s) => normalizeExamSession(s, coreExamSets))
      .filter(Boolean);
  }, [activeExamSessions, coreExamSets]);

  const practiceByDataset = useMemo(() => {
    const map = {};
    activePracticeSessions.forEach((s) => { map[s.datasetId] = s; });
    return map;
  }, [activePracticeSessions]);

  const mockByDataset = useMemo(() => {
    const map = {};
    activeMockSessions.forEach((s) => { map[s.datasetId] = s; });
    return map;
  }, [activeMockSessions]);

  const allActiveSessions = useMemo(() => {
    return [...activePracticeSessions, ...activeMockSessions]
      .sort((a, b) => sessionTimeValue(b) - sessionTimeValue(a));
  }, [activePracticeSessions, activeMockSessions]);

  const stats = useMemo(() => {
    const examAttempts = attempts.filter((row) => row.mode !== 'study');
    const practiceAttempts = attempts.filter((row) => row.mode === 'study');
    const completed = examAttempts.length;
    const bestScore = completed ? Math.max(...examAttempts.map((row) => row.score_percent || 0)) : null;
    const passed = examAttempts.filter((row) => row.passed).length;
    const practiceCount = Math.max(currentUser?.practice_used || 0, practiceAttempts.length);
    const weekStart = new Date();
    weekStart.setDate(weekStart.getDate() - 6);
    weekStart.setHours(0, 0, 0, 0);
    const weekly = attempts.filter((row) => new Date(row.submitted_at) >= weekStart);
    const weeklyMinutes = Math.round(weekly.reduce((sum, row) => sum + (row.duration_seconds || 0), 0) / 60);
    const activeDays = new Set(weekly.map((row) => new Date(row.submitted_at).toDateString())).size;
    const completedSets = new Set(examAttempts.map((row) => row.dataset_id).filter(Boolean)).size;
    return { completed, bestScore, passed, practiceCount, weeklyMinutes, activeDays, streak: countStreak(attempts), completedSets };
  }, [attempts, currentUser]);

  const remainingQuota = useMemo(() => getExamQuotaStatus(currentUser), [currentUser]);

  const importantNotifications = useMemo(
    () => buildStudentNotifications({ currentUser, assignments, remainingQuota }),
    [currentUser, assignments, remainingQuota],
  );

  const clearPracticeSession = useCallback((datasetId) => {
    if (!datasetId) return;
    try {
      const uid = getEffectiveUserId(currentUser);
      if (!uid) return;
      const userPrefix = `sao_viet_study_v5_${uid}_`;
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i);
        if (k && k.startsWith(userPrefix) && k.includes(datasetId)) {
          localStorage.removeItem(k);
        }
      }
    } catch (e) {}
  }, [currentUser]);

  const openSelection = useCallback(() => {
    setPage('selection');
    window.setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 0);
  }, []);

  const openDashboard = useCallback(() => {
    setPage('dashboard');
    window.setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 0);
  }, []);

  const openProgress = useCallback(async () => {
    setPage('progress');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setProgressLoading(true);
    setProgressError('');
    try {
      setLearningProgress(await getLearningProgress(progressPeriod));
    } catch (err) {
      setProgressError(err.response?.data?.detail || 'Không tải được dữ liệu tiến bộ.');
    } finally {
      setProgressLoading(false);
    }
  }, [progressPeriod]);

  const changeProgressPeriod = useCallback(async (nextPeriod) => {
    setProgressPeriod(nextPeriod);
    setProgressLoading(true);
    setProgressError('');
    try { setLearningProgress(await getLearningProgress(nextPeriod)); } catch (err) { setProgressError(err.response?.data?.detail || 'Không tải được dữ liệu tiến bộ.'); } finally { setProgressLoading(false); }
  }, []);

  const openAttemptDetail = useCallback(async (attemptId) => {
    try { setAttemptDetail(await getAttempt(attemptId)); } catch (err) { setError('Không mở được chi tiết bài làm.'); }
  }, []);

  const reviewExamAttempt = useCallback((datasetId, attemptId) => {
    if (!attemptId) return;
    const params = new URLSearchParams({ mode: 'exam', attempt_id: String(attemptId) });
    if (datasetId) params.set('dataset_id', datasetId);
    navigate(`/quiz?${params.toString()}`);
  }, [navigate]);

  const startSmartReview = useCallback(() => {
    const recommendation = learningSummary?.recommended_session || {};
    const params = new URLSearchParams({ mode: 'smart-review' });
    if (recommendation.dataset_id) params.set('dataset_id', recommendation.dataset_id);
    if (recommendation.topic) params.set('topic', recommendation.topic);
    navigate(`/quiz?${params.toString()}`);
  }, [learningSummary, navigate]);

  const navigateToWork = useCallback((mode, datasetId, replace = false, sessionId = null, options = {}) => {
    const queryMode = modeToQuery(mode);
    const replaceParam = replace && mode === 'mock' ? '&replace=1' : '';
    const sessionParam = sessionId && mode === 'mock' ? `&session_id=${sessionId}` : '';
    const resumeParam = mode === 'practice' && options.resume ? '&resume=1' : '';
    const randomParam = mode === 'practice' && typeof options.random === 'boolean'
      ? `&random=${options.random ? '1' : '0'}`
      : '';
    navigate(`/quiz?dataset_id=${datasetId}&mode=${queryMode}${replaceParam}${sessionParam}${resumeParam}${randomParam}`);
  }, [navigate]);

  const continueSession = useCallback((session) => {
    if (!session) return;
    navigateToWork(session.mode, session.datasetId, false, session.id, {
      resume: true,
      random: Boolean(session.isRandom),
    });
  }, [navigateToWork]);

  const requestRestart = useCallback((session) => {
    if (!session) return;
    setPage('selection');
    setSelectedExamSetId(session.datasetId);
    setSelectedMode(session.mode);
    setReplaceIntent({
      mode: session.mode,
      selectedDataset: coreExamSets.find((dataset) => dataset.id === session.datasetId),
      existingSession: session,
    });
  }, [coreExamSets]);

  const startSelected = useCallback(() => {
    if (!selectedExamSetId || !selectedMode) return;
    if (selectedMode === 'mock' && remainingQuota?.isEmpty) {
      setError(`Bạn đã sử dụng hết số lượt thi thử được cấp (${remainingQuota.used}/${remainingQuota.total} lượt). Vui lòng chọn Ôn tập hoặc liên hệ trung tâm để được cộng thêm lượt.`);
      return;
    }
    const existingSession = selectedMode === 'mock'
      ? mockByDataset[selectedExamSetId]
      : practiceByDataset[selectedExamSetId];

    if (existingSession) {
      const selectedDataset = coreExamSets.find((dataset) => dataset.id === selectedExamSetId);
      setReplaceIntent({ mode: selectedMode, selectedDataset, existingSession });
      return;
    }
    navigateToWork(selectedMode, selectedExamSetId, false, null, {
      resume: false,
      random: randomizePractice,
    });
  }, [coreExamSets, mockByDataset, navigateToWork, practiceByDataset, randomizePractice, remainingQuota, selectedExamSetId, selectedMode]);

  const chooseExamSetMode = useCallback((datasetId, mode) => {
    setSelectedExamSetId(datasetId);
    setSelectedMode(mode);
  }, []);

  const createNewFromIntent = useCallback(async () => {
    if (!replaceIntent) return;
    const datasetId = replaceIntent.selectedDataset?.id || selectedExamSetId;
    const mode = replaceIntent.mode;
    setReplaceIntent(null);
    if (mode === 'practice') {
      clearPracticeSession(datasetId);
      navigateToWork('practice', datasetId, false, null, {
        resume: false,
        random: randomizePractice,
      });
      return;
    }
    navigateToWork('mock', datasetId, true);
  }, [clearPracticeSession, navigateToWork, randomizePractice, replaceIntent, selectedExamSetId]);

  const requestPracticeAgain = useCallback((datasetId) => {
    const targetId = datasetId || coreExamSets[0]?.id;
    if (!targetId) return;
    setPage('selection');
    setSelectedExamSetId(targetId);
    setSelectedMode('practice');
    const existing = practiceByDataset[targetId];
    if (existing) {
      setReplaceIntent({
        mode: 'practice',
        selectedDataset: coreExamSets.find((dataset) => dataset.id === targetId),
        existingSession: existing,
      });
    } else {
      navigateToWork('practice', targetId, false, null, {
        resume: false,
        random: randomizePractice,
      });
    }
  }, [coreExamSets, navigateToWork, practiceByDataset, randomizePractice]);

  const confirmCancelSession = useCallback(async () => {
    if (!cancelSession) return;
    try {
      if (cancelSession.mode === 'practice') {
        if (cancelSession.storageKey) localStorage.removeItem(cancelSession.storageKey);
      } else {
        await abandonExamSession(cancelSession.id);
      }
      setCancelSession(null);
      await load();
      setToast('Đã hủy phiên làm bài.');
      window.setTimeout(() => setToast(''), 2800);
    } catch (err) {
      setError(err.response?.data?.detail || 'Chưa hủy được phiên. Vui lòng thử lại.');
    }
  }, [cancelSession, load]);

  const startButtonDisabled = !selectedExamSetId || !selectedMode || loading;
  const selectedDataset = coreExamSets.find((dataset) => dataset.id === selectedExamSetId);
  const selectedDatasetIndex = coreExamSets.findIndex((dataset) => dataset.id === selectedExamSetId);
  const selectedSetLabel = selectedDatasetIndex >= 0 ? `Đề số ${selectedDatasetIndex + 1}` : 'Chưa chọn';
  const selectedModeLabel = selectedMode ? modeLabel(selectedMode) : 'Chưa chọn';
  const hasSavedSessions = allActiveSessions.length > 0;
  const scrollToExamPicker = useCallback(() => {
    document.getElementById('exam-picker')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  return (
    <div className="learning-shell">
      <AppHeader
        activePage={page}
        currentUser={currentUser}
        importantNotifications={importantNotifications}
        examAttemptCount={stats.completed}
        onOpenDashboard={openDashboard}
        onOpenSelection={openSelection}
        onOpenExamHistory={() => setHistoryModalDataset('all')}
        onOpenProgress={openProgress}
        smartReviewEnabled={Boolean(learningSummary?.enabled)}
        logout={logout}
      />

      {toast && <div className="learning-toast success" role="status"><CheckCircleIcon /> {toast}</div>}
      <main className={`learning-main ${page === 'selection' ? 'selection-premium-page' : page === 'dashboard' ? 'dashboard-premium-page' : ''}`}>
        {page === 'dashboard' ? (
          <>
            <HeroBanner
              currentUser={currentUser}
              examAttemptCount={stats.completed}
              onOpenSelection={openSelection}
              onOpenExamHistory={() => setHistoryModalDataset('all')}
            />

            <section className="achievement-grid" aria-label="Thành tích học tập">
              <StatCard
                tone="blue"
                icon={ClipboardDocumentCheckIcon}
                label="Bài thi hoàn thành"
                value={stats.completed}
                unit=" bài"
                desc="bấm để xem lại lịch sử thi"
                onClick={() => setHistoryModalDataset('all')}
              />
              <StatCard tone="purple" icon={AcademicCapIcon} label="Lượt luyện tập" value={stats.practiceCount} unit=" lượt" desc="ôn tự do không trừ quota" />
              <StatCard tone="indigo" icon={ChartBarIcon} label="Điểm thi cao nhất" value={stats.bestScore === null ? '--' : `${stats.bestScore}%`} unit="" desc={stats.bestScore === null ? 'chưa thi thử' : 'kết quả thi tốt nhất'} />
              <StatCard tone="green" icon={TrophyIcon} label="Lượt đạt chuẩn" value={stats.passed} unit=" lượt" desc="đạt chuẩn từ 70%" />
              <QuotaStatCard quota={remainingQuota} />
            </section>

            {error && <div className="learning-alert">{error}<button onClick={load} type="button">Thử lại</button></div>}
            {loading && <DashboardSkeleton />}

            {!loading && (
              <>
                <section className="focus-grid">
                  <CurrentSessionsPanel
                    sessions={allActiveSessions}
                    onOpenSelection={openSelection}
                    onContinue={continueSession}
                    onRestart={requestRestart}
                    onCancel={setCancelSession}
                  />
                  <WeeklyGoalCard stats={stats} />
                </section>

                <section className="dashboard-secondary-grid">
                  <LearningSuggestions
                    sessions={allActiveSessions}
                    attempts={attempts}
                    datasets={coreExamSets}
                    summary={learningSummary}
                    onContinue={continueSession}
                    onPracticeAgain={requestPracticeAgain}
                    onStartReview={startSmartReview}
                    onOpenSelection={openSelection}
                  />
                  <AchievementPanel stats={stats} />
                </section>

                <RecentActivityCard
                  attempts={attempts}
                  datasets={coreExamSets}
                  onPracticeAgain={requestPracticeAgain}
                  onReviewAttempt={reviewExamAttempt}
                  onOpenProgress={openProgress}
                  onOpenSelection={openSelection}
                />
              </>
            )}
          </>
        ) : page === 'progress' ? (
          <ProgressView
            progress={learningProgress}
            period={progressPeriod}
            loading={progressLoading}
            error={progressError}
            onRetry={() => changeProgressPeriod(progressPeriod)}
            onPeriodChange={changeProgressPeriod}
            onOpenAttempt={openAttemptDetail}
            onOpenDashboard={openDashboard}
          />
        ) : (
          <>
            <SelectionHero hasSavedSessions={hasSavedSessions} hasSelection={Boolean(selectedExamSetId && selectedMode)} />
            {error && <div className="learning-alert">{error}<button onClick={() => setError('')} type="button">Đóng</button></div>}

            <section className="selection-panel session-first-panel">
              <div className="selection-section-heading">
                <span>1</span>
                <div>
                  <h2>{hasSavedSessions ? 'Bạn có bài đang dở' : 'Chưa có bài đang dở'}</h2>
                  <p>{hasSavedSessions ? 'Ưu tiên tiếp tục để không mất mạch học. Nếu muốn làm bài mới, hãy chọn bộ đề bên dưới.' : 'Bạn có thể bắt đầu bài mới bằng cách chọn một bộ đề và chế độ làm bài.'}</p>
                </div>
              </div>
              {hasSavedSessions ? (
                <div className="saved-session-grid compact">
                  {allActiveSessions.map((session) => (
                    <SavedSessionCard
                      key={`${session.mode}-${session.datasetId}`}
                      title={session.mode === 'mock' ? `Thi thử · ${extractSetShortLabel(session.examSetTitle)}` : `Ôn tập · ${extractSetShortLabel(session.examSetTitle)}`}
                      session={session}
                      mode={session.mode}
                      onContinue={continueSession}
                      onRestart={requestRestart}
                    />
                  ))}
                </div>
              ) : (
                <div className="selection-empty-inline">
                  <BookOpenIcon />
                  <span><strong>Sẵn sàng tạo bài mới</strong>Chọn một bộ đề phía dưới, sau đó bấm Ôn tập hoặc Thi thử ngay trong card.</span>
                </div>
              )}
              <div className="session-first-actions">
                <button className="secondary-action" onClick={scrollToExamPicker} type="button">
                  <ArrowRightIcon /> {hasSavedSessions ? 'Tạo bài mới' : 'Chọn bộ đề'}
                </button>
              </div>
            </section>

            <section className="selection-panel" id="exam-picker">
              <div className="selection-section-heading">
                <span>2</span>
                <div>
                  <h2>Chọn bộ đề và chế độ</h2>
                  <p>Mỗi bộ đề có sẵn 2 lựa chọn. Ôn tập để học từng câu, Thi thử để làm bài tính giờ.</p>
                </div>
              </div>
              <div className="selection-set-grid">
                {coreExamSets.map((dataset, index) => (
                  <ExamSetCard
                    key={dataset.id}
                    dataset={dataset}
                    index={index}
                    attempts={attempts}
                    practiceSession={practiceByDataset[dataset.id]}
                    mockSession={mockByDataset[dataset.id]}
                    selected={selectedExamSetId === dataset.id}
                    selectedMode={selectedMode}
                    mockDisabled={Boolean(remainingQuota?.isEmpty)}
                    onSelect={setSelectedExamSetId}
                    onSelectMode={chooseExamSetMode}
                    onReviewAttempt={reviewExamAttempt}
                    onOpenHistory={setHistoryModalDataset}
                  />
                ))}
              </div>
            </section>

            <section className="selection-panel start-panel">
              <div className="selection-section-heading">
                <span>3</span>
                <div>
                  <h2>Bắt đầu làm bài</h2>
                  <p>Nút chỉ bật khi đã chọn đủ bộ đề và chế độ.</p>
                </div>
              </div>
              <div className="selection-start-summary" aria-label="Thông tin bài làm đã chọn">
                <div className="summary-item">
                  <div className="summary-icon blue"><DocumentTextIcon /></div>
                  <div>
                    <span>Bộ đề đã chọn</span>
                    <strong>{selectedSetLabel}</strong>
                    <small>{selectedDataset ? compactExamTitle(selectedDataset.title) : 'Chọn một bộ đề ở bước 2'}</small>
                  </div>
                </div>
                <div className="summary-item">
                  <div className={`summary-icon ${selectedMode === 'mock' ? 'orange' : 'blue'}`}>
                    {selectedMode === 'mock' ? <FireIcon /> : <AcademicCapIcon />}
                  </div>
                  <div>
                    <span>Chế độ làm bài</span>
                    <strong>{selectedModeLabel}</strong>
                    <small>
                      {selectedMode === 'mock'
                        ? 'Thi thử tính giờ · Tự động trộn đề'
                        : selectedMode === 'practice'
                          ? (randomizePractice ? 'Ôn từng câu · Trộn ngẫu nhiên' : 'Ôn từng câu · Theo thứ tự đề')
                          : 'Chọn Ôn tập hoặc Thi thử'}
                    </small>
                  </div>
                </div>
                <div className="summary-item">
                  <div className="summary-icon green"><TrophyIcon /></div>
                  <div>
                    <span>Mục tiêu</span>
                    <strong>{DEFAULT_QUESTION_COUNT} câu · {DEFAULT_DURATION_MINUTES} phút · Đạt {PASS_THRESHOLD}%</strong>
                    <small>Sẵn sàng khi bạn bấm bắt đầu</small>
                  </div>
                </div>
              </div>

              {selectedMode === 'practice' && (
                <div
                  className={`practice-random-config ${randomizePractice ? 'is-random' : 'is-sequential'}`}
                  aria-label="Tùy chọn sắp xếp câu hỏi khi ôn tập"
                >
                  <div className="practice-random-info">
                    <div className={`summary-icon ${randomizePractice ? 'blue' : 'green'}`}>
                      {randomizePractice ? <ArrowPathIcon /> : <CheckBadgeIcon />}
                    </div>
                    <div className="practice-random-copy">
                      <span className="practice-random-eyebrow">
                        Sắp xếp câu hỏi
                        <em className={`random-status-pill ${!randomizePractice ? 'on' : 'off'}`}>
                          {!randomizePractice ? 'Mặc định' : 'Trộn ngẫu nhiên'}
                        </em>
                      </span>
                      <strong>
                        {!randomizePractice
                          ? 'Hiển thị lần lượt theo thứ tự bộ đề (Khuyên dùng)'
                          : 'Trộn ngẫu nhiên câu hỏi và đáp án'}
                      </strong>
                      <small>
                        {!randomizePractice
                          ? 'Câu hỏi và các phương án trả lời được giữ nguyên vị trí từ câu 1 đến câu 50 chuẩn theo bộ đề.'
                          : 'Đảo vị trí câu hỏi và các phương án lựa chọn mỗi lần ôn tập để rèn phản xạ.'}
                      </small>
                    </div>
                  </div>
                  <div className="practice-order-pills" role="group" aria-label="Chọn chế độ sắp xếp câu hỏi">
                    <button
                      type="button"
                      className={`order-pill-btn ${!randomizePractice ? 'active sequential' : ''}`}
                      onClick={() => setRandomizePractice(false)}
                    >
                      <CheckBadgeIcon />
                      <span>Theo thứ tự đề (Mặc định)</span>
                    </button>
                    <button
                      type="button"
                      className={`order-pill-btn ${randomizePractice ? 'active' : ''}`}
                      onClick={() => setRandomizePractice(true)}
                    >
                      <ArrowPathIcon />
                      <span>Trộn ngẫu nhiên</span>
                    </button>
                  </div>
                </div>
              )}

              {selectedMode === 'mock' && (
                <div className="mock-random-notice-box" role="note" aria-label="Lưu ý trộn ngẫu nhiên khi thi thử">
                  <div className="practice-random-info">
                    <div className="summary-icon orange"><InformationCircleIcon /></div>
                    <div className="practice-random-copy">
                      <span className="practice-random-eyebrow">
                        Quy định chế độ Thi thử
                        <em className="random-status-pill mock">Tự động đảo đề</em>
                      </span>
                      <strong>Tự động trộn ngẫu nhiên câu hỏi và đáp án</strong>
                      <small>
                        Khi thi thử tính giờ, hệ thống tự động đảo vị trí các câu hỏi và phương án trả lời nhằm mô phỏng sát kỳ thi thực tế.
                      </small>
                    </div>
                  </div>
                  <div className="mock-random-badge">
                    <ArrowPathIcon />
                    <span>Đề trộn ngẫu nhiên</span>
                  </div>
                </div>
              )}

              {selectedExamSetId && (() => {
                const selectedSetAttempts = (attempts || []).filter(
                  (row) => row.dataset_id === selectedExamSetId && row.mode !== 'study'
                );
                if (!selectedSetAttempts.length) return null;
                return (
                  <div
                    style={{
                      background: '#f8fafc',
                      border: '1px solid #bfdbfe',
                      borderRadius: '14px',
                      padding: '14px 18px',
                      marginBottom: '16px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
                      <strong style={{ fontSize: '14px', color: '#1e3a8a', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        <ClockIcon style={{ width: '17px', height: '17px', color: '#2563eb' }} />
                        Lịch sử các lần thi thử {selectedSetLabel} ({selectedSetAttempts.length} lần đã thi)
                      </strong>
                      <span style={{ fontSize: '12px', color: '#475569', fontWeight: 600 }}>
                        Xem lại bài thi hoàn toàn miễn phí — chỉ khi bấm &quot;Bắt đầu làm bài&quot; mới trừ 1 lượt thi
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      {selectedSetAttempts.map((att, idx) => {
                        const attemptNum = selectedSetAttempts.length - idx;
                        return (
                          <button
                            key={att.attempt_id}
                            type="button"
                            onClick={() => reviewExamAttempt(selectedExamSetId, att.attempt_id)}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '8px 14px',
                              borderRadius: '10px',
                              border: '1px solid #93c5fd',
                              background: '#ffffff',
                              color: '#1e293b',
                              fontSize: '13px',
                              fontWeight: 700,
                              cursor: 'pointer',
                            }}
                          >
                            <span style={{ color: '#2563eb' }}>Lần #{attemptNum}:</span>
                            <span
                              style={{
                                padding: '2px 7px',
                                borderRadius: '999px',
                                fontSize: '12px',
                                background: att.passed ? '#dcfce7' : '#fee2e2',
                                color: att.passed ? '#15803d' : '#b91c1c',
                              }}
                            >
                              {att.score_percent}% ({att.correct_questions}/{att.total_questions})
                            </span>
                            <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 500 }}>
                              {formatDate(att.submitted_at)} · Xem lại →
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              <div className="selection-info">
                <DocumentTextIcon />
                <span>Tiến trình ôn luyện và thi thử của mỗi đề được lưu độc lập và tự động. Bạn có thể yên tâm học tiếp bất cứ lúc nào.</span>
              </div>
              <div className="selection-footer-actions">
                <button className="task-cta selection-start" disabled={startButtonDisabled} onClick={startSelected} type="button">
                  <PlayIcon /> {loading ? 'Đang chuẩn bị...' : 'Bắt đầu làm bài'}
                </button>
                <button className="secondary-action selection-back" onClick={openDashboard} type="button">
                  <ArrowLeftIcon /> Quay lại tổng quan
                </button>
                <p>{!selectedExamSetId ? 'Hãy chọn bộ đề trước.' : !selectedMode ? 'Hãy chọn Ôn tập hoặc Thi thử.' : `Sẵn sàng bắt đầu ${modeLabel(selectedMode)} với ${selectedDataset?.title || 'bộ đề đã chọn'}.`}</p>
              </div>
            </section>
          </>
        )}
      </main>

      <footer className="learning-footer">
        <span>© 2026 Sao Việt. All rights reserved.</span>
        <nav aria-label="Liên kết hỗ trợ">
          <a href="#support">Hỗ trợ</a>
          <a href="#terms">Điều khoản</a>
          <a href="#privacy">Bảo mật</a>
        </nav>
      </footer>

      <ConfirmReplaceSessionModal
        intent={replaceIntent}
        randomizePractice={randomizePractice}
        onChangeRandomizePractice={setRandomizePractice}
        onClose={() => setReplaceIntent(null)}
        onContinue={continueSession}
        onCreateNew={createNewFromIntent}
      />
      <ConfirmCancelSessionModal
        session={cancelSession}
        onClose={() => setCancelSession(null)}
        onConfirm={confirmCancelSession}
      />
      <AttemptDetailModal
        detail={attemptDetail}
        onClose={() => setAttemptDetail(null)}
        onReviewAttempt={reviewExamAttempt}
      />
      <ExamHistoryModal
        dataset={historyModalDataset}
        datasets={coreExamSets}
        attempts={attempts}
        onClose={() => setHistoryModalDataset(null)}
        onReviewAttempt={reviewExamAttempt}
      />
    </div>
  );
};

export default TrainingPortal;
